import { normalizeForexTicker } from "./analysis/assetClass.ts";
export function calculateConfidenceScore(technicalScore: number, fundamentalScore: number): number {
  if (technicalScore < 0 || fundamentalScore < 0) return 0;
  if (technicalScore > 100 || fundamentalScore > 100) return 100;

  // Example weighting
  const combined = (technicalScore * 0.4) + (fundamentalScore * 0.6);
  return Math.round(combined);
}

export function formatTrend(score: number): 'BULLISH' | 'BEARISH' | 'NEUTRAL' {
  if (score > 60) return 'BULLISH';
  if (score < 40) return 'BEARISH';
  return 'NEUTRAL';
}

export function normalizeCurrencyValue(value: number, currency: string = 'USD'): number {
  let ccy = currency || 'USD';
  let divisor = 1;

  if (ccy === 'GBp') { divisor = 100; }
  else if (ccy === 'ZAc') { divisor = 100; }
  else if (ccy === 'ILA') { divisor = 100; }
  else if (ccy === 'IEp') { divisor = 100; }

  return value / divisor;
}

export function formatCurrency(price: number, currency: string = 'USD'): string {
  try {
    let ccy = currency || 'USD';
    let divisor = 1;

    if (ccy === 'GBp') { ccy = 'GBP'; divisor = 100; }
    else if (ccy === 'ZAc') { ccy = 'ZAR'; divisor = 100; }
    else if (ccy === 'ILA') { ccy = 'ILS'; divisor = 100; }
    else if (ccy === 'IEp') { ccy = 'EUR'; divisor = 100; }

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: ccy,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: ccy === 'JPY' ? 0 : 2,
      maximumFractionDigits: ccy === 'JPY' ? 0 : 2
    }).format(price / divisor);
  } catch (e) {
    return `${currency} ${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}

export function formatYTD(ytd: number | null | undefined): string {
  if (ytd == null) return '';
  return `${ytd >= 0 ? '+' : ''}${(ytd * 100).toFixed(2)}% YTD`;
}

export function formatPerf(val: number | null | undefined, label: string): string {
  if (val == null) return '';
  return `${val >= 0 ? '+' : ''}${(val * 100).toFixed(2)}% ${label}`;
}

export function isUSTicker(ticker: string): boolean {
  if (!ticker) return false;
  const upper = ticker.toUpperCase();
  // Yahoo Finance US tickers typically don't have a dot suffix, 
  // or they have common US exchange suffixes like .N (NYSE), .O (Nasdaq), .AR (Arca), .P (Pacific)
  // Foreign tickers often have suffixes like .DE, .L, .PA, .HK, etc.
  return !upper.includes('.') ||
    upper.endsWith('.O') ||
    upper.endsWith('.N') ||
    upper.endsWith('.AR') ||
    upper.endsWith('.P');
}

export function isIndexTicker(ticker: string): boolean {
  if (!ticker) return false;
  const upper = ticker.trim().toUpperCase();
  return upper.startsWith('^') || upper.includes('%5E');
}

export const ADR_FUNDAMENTALS_MAP: Record<string, string> = {
  // United Kingdom (LSE)
  "BP.L": "BP",
  "RIO.L": "RIO",
  "SHEL.L": "SHEL",
  "AZN.L": "AZN",
  "GSK.L": "GSK",
  "ULVR.L": "UL",
  "DGE.L": "DEO",
  "BATS.L": "BTI",
  "HSBA.L": "HSBC",
  "REL.L": "RELX",
  "NG.L": "NGG",
  "VOD.L": "VOD",
  "BARC.L": "BCS",
  "LLOY.L": "LYG",
  "NWG.L": "NWG",
  "PRU.L": "PUK",
  "IHG.L": "IHG",
  "WPP.L": "WPP",
  "RR.L": "RYCEY",
  "BA.L": "BAESY",
  "EXPN.L": "EXPGY",
  "GLEN.L": "GLNCY",

  // Australia (ASX)
  "RIO.AX": "RIO",
  "BHP.AX": "BHP",
  "BHP.L": "BHP",
  "WDS.AX": "WDS",
  "JHX.AX": "JHX",
  "CSL.AX": "CSLLY",
  "MQG.AX": "MQBKY",
  "CBA.AX": "CMWAY",

  // Continental Europe
  "ASML.AS": "ASML",
  // Swiss secondary line: Yahoo has no forward EPS and negative FCF for it, so unmapped it read -52% against -12%.
  "ASML.SW": "ASML",
  "INGA.AS": "ING",
  "TTE.PA": "TTE",
  "SAN.PA": "SNY",
  "MC.PA": "LVMUY",
  "AIR.PA": "EADSY",
  "OR.PA": "LRLCY",
  "SU.PA": "SBGSY",
  "BNP.PA": "BNPQY",
  "SAP.DE": "SAP",
  "SIE.DE": "SIEGY",
  "DTE.DE": "DTEGY",
  "BAS.DE": "BASFY",
  "ALV.DE": "ALIZY",
  "SON1.F": "SONY",
  "NOVN.SW": "NVS",
  "UBSG.SW": "UBS",
  "NESN.SW": "NSRGY",
  // Yahoo quotes Roche's Genussschein as ROP.SW (there is no ROG.SW); RHHBY is 1/8 of it.
  "ROP.SW": "RHHBY",
  "NOVO-B.CO": "NVO",
  "ERIC-B.ST": "ERIC",
  "NOKIA.HE": "NOK",

  // Asia / Pacific & Emerging Markets
  "2330.TW": "TSM",
  "7203.T": "TM",
  "6758.T": "SONY",
  "8306.T": "MUFG",
  "8316.T": "SMFG",
  "7267.T": "HMC",
  "INFY.NS": "INFY",
  "HDFCBANK.NS": "HDB",
  "ICICIBANK.NS": "IBN",

  // Secondary listings of mapped and US names (v20.19, validated 2026-10-07): each valued as-is and mapped against
  // its primary; mapped only where the unmapped line was more than 10 points off the primary, mapping brought it
  // within 10, and the inferred ratio matched the price ratio. Thin secondary lines lack forward EPS and FMP data.
  // Milan (Borsa Italiana, EUR)
  "1AMD.MI": "AMD",
  "1AMZN.MI": "AMZN",
  "1ASML.MI": "ASML",
  "1AVGO.MI": "AVGO",
  "1COST.MI": "COST",
  "1DTE.MI": "DTEGY",
  "1INGA.MI": "ING",
  "1INTC.MI": "INTC",
  "1JNJ.MI": "JNJ",
  "1JPM.MI": "JPM",
  "1LLY.MI": "LLY",
  "1MA.MI": "MA",
  "1MC.MI": "LVMUY",
  "1MSFT.MI": "MSFT",
  "1NESN.MI": "NSRGY",
  "1NFLX.MI": "NFLX",
  "1NOVN.MI": "NVS",
  "1ORCL.MI": "ORCL",
  "1PEP.MI": "PEP",
  "1PG.MI": "PG",
  "1RO.MI": "RHHBY",
  "1SIE.MI": "SIEGY",
  "1TSLA.MI": "TSLA",
  "1UBSG.MI": "UBS",
  "1UNH.MI": "UNH",
  "1V.MI": "V",
  "1WMT.MI": "WMT",
  "1XOM.MI": "XOM",
  // Frankfurt (EUR)
  "7DZ.F": "XOM",
  "AIR.F": "EADSY",
  "AMD.F": "AMD",
  "BAS.F": "BASFY",
  "BCY.F": "BCS",
  "BPE5.F": "BP",
  "CSJ.F": "CSLLY",
  "CSJA.F": "CSLLY",
  "CWW0.F": "CMWAY",
  "IC1H.F": "IHG",
  "ICBA.F": "IBN",
  "J2BA.F": "EXPGY",
  "JNJ.F": "JNJ",
  "LLD2.F": "LYG",
  "LLY.F": "LLY",
  "M4I.F": "MA",
  "MSF.F": "MSFT",
  "NNGF.F": "NGG",
  "NOV.F": "NVO",
  "PRG.F": "PG",
  "PRU.F": "PUK",
  "RIO1.F": "RIO",
  "RRU.F": "RYCEY",
  "RYS.F": "NWG",
  "RYSD.F": "NWG",
  "SIE.F": "SIEGY",
  "SIEB.F": "SIEGY",
  "SND.F": "SBGSY",
  "SONA.F": "SONY",
  "UNH.F": "UNH",
  "WMT.F": "WMT",
  // SIX Swiss (CHF)
  "AMD.SW": "AMD",
  "AMZN.SW": "AMZN",
  "INTC.SW": "INTC",
  "MSFT.SW": "MSFT",
  "XOM.SW": "XOM",
  // v20.25 (validated 2026-10-07 against the main local listing, not the US ADR, so currency-rate and listing-premium
  // gaps do not count against the mapping): each lands within 10 points of its local line. Five need the stored ratio
  // below because their cash data cannot support inference.
  "ZEG.F": "AZN",
  "BSPA.F": "BAESY",
  "SNW.F": "SNY",
  "ALVE.F": "ALIZY",
  "NOTA.F": "NVS",
  "NESM.F": "NSRGY",
  "NNGD.F": "NGG",
  "0WPA.F": "WPP",
  "IOY.F": "INFY",
  "HDFA.F": "HDB",
  "PEP.F": "PEP",
  "1SAN.MI": "SNY",
  "1SAP.MI": "SAP",
  "1AAPL.MI": "AAPL",
  "1GOOGL.MI": "GOOGL",
  "1ALV.MI": "ALIZY",
  "MC.SW": "LVMUY",
  // v20.31 (A77, validated 2026-10-07): TSFA.F trades at TSM's own price, so it is judged against TSM (lands +0.9% vs
  // -4.4%; unmapped -98%); TOM.F is an ordinary Toyota share (-12.5% vs 7203.T -7.4%; unmapped -56%); 1PRU.MI is
  // Prudential Financial, not Prudential plc (+38.7% vs PRU +34.4%; unmapped +59%).
  "TSFA.F": "TSM",
  "TOM.F": "TM",
  "1PRU.MI": "PRU",
  // v20.44 (A84, validated 2026-10-08 with the home-listing rate): ROP.F and 1ROP.MI are Roper Technologies (+56% vs ROP
  // +54%), HMC.F is Harmonic (+126% vs HLIT +119%), AAPL.SW -37% vs AAPL -37%, SAN.SW +71% vs SAN.PA +71%.
  "ROP.F": "ROP",
  "1ROP.MI": "ROP",
  "HMC.F": "HLIT",
  "AAPL.SW": "AAPL",
  "SAN.SW": "SNY",
  // v20.49 (A86, validated 2026-10-10): 1BA.MI is Boeing, mapped to BA (lands within a point of BA with primary forward EPS).
  "1BA.MI": "BA"
};

/**
 * v20.44 (A84): Zurich lines of companies listed elsewhere (`isSecondaryListing`, `riskFreeCurrency.ts`). Frankfurt (`.F`)
 * and Milan's foreign segment (`1*.MI`) are recognised by symbol; a `.SW` line is a home listing unless it is here (Novartis,
 * UBS, ABB and Logitech report in USD but are Swiss).
 */
export const SWISS_SECONDARY_LISTINGS: ReadonlySet<string> = new Set([
  "ASML.SW", "AMD.SW", "AMZN.SW", "INTC.SW", "MSFT.SW", "XOM.SW", "MC.SW", "AAPL.SW", "SAN.SW",
]);

/**
 * v20.25: local listings whose ordinary-shares-per-ADR ratio enrichment cannot infer (thin secondary lines carry no
 * cash data, so inference falls back to 1). Value = local shares per mapped ADR, taken from the price ratio and checked
 * by `npm run audit:adr`. Used in place of the inferred ratio.
 */
export const ADR_RATIO_OVERRIDES: Record<string, number> = {
  "NNGD.F": 1,   // inferred 6
  "0WPA.F": 1,   // inferred 5
  "IOY.F": 1,    // inferred 2
  "HDFA.F": 1,   // inferred 5
  "1ALV.MI": 0.1, // inferred 1; ALIZY is a tenth of a share
  "1GOOGL.MI": 1, // inferred 2: Yahoo counts only Class A shares for GOOGL, half of Alphabet's total
  "TSFA.F": 1,    // inferred 8: an ADR-equivalent line (5 ordinary shares) priced at TSM's ADR price
};

/**
 * v20.21 (CRITIQUE A70): US-listed ADRs whose Yahoo forward EPS and earnings-trend consensus are per ORDINARY share
 * while their trailing EPS is per ADR. Value = ordinary shares per ADR, the factor that puts the forward figures in
 * ADR units. Found by the cross-listing invariant (forward / trailing EPS is unit-free, so an ADR and its local line
 * should agree): PUK read 0.57 against PRU.L's 0.97, i.e. half, and priced on a halved forward EPS (-40%).
 * `npm run audit:adr` re-checks every pair and flags an entry here once Yahoo corrects its units.
 */
export const ADR_FORWARD_EPS_PER_ORDINARY: Record<string, number> = {
  PUK: 2,
};

/**
 * Maps tickers that have been delisted, renamed, or moved to a different primary exchange
 * to their currently active Yahoo Finance equivalent.
 * Used by the analysis engine to transparently redirect stale tickers.
 * Key = old/defunct ticker, Value = active Yahoo Finance ticker.
 */
export const TICKER_REDIRECT_MAP: Record<string, string> = {
  // LNW (Light & Wonder) delisted from Nasdaq in Nov 2025; primary listing moved to ASX
  "LNW": "LNW.AX",
  // ABB delisted from NYSE in May 2023; primary listing on Swiss Exchange
  "ABB": "ABBN.SW",
};

export function cleanString(str: string): string {
  return str.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
}

export function areThemesSimilar(themeA: string, themeB: string): boolean {
  const a = cleanString(themeA);
  const b = cleanString(themeB);

  if (a === b) return true;

  // Direct substring check
  if (a.includes(b) || b.includes(a)) {
    const shorter = a.length < b.length ? a : b;
    if (shorter.length >= 4) return true;
  }

  // Specific known finance/tech abbreviations and alternates
  const wordSynonyms: Record<string, string[]> = {
    financial: ["finance", "financials", "financial services"],
    tech: ["technology", "tech services", "technology services"],
    health: ["healthcare", "health care", "health"],
    commun: ["communication", "communications", "communication services"],
    staples: ["consumer defensive", "consumer staples", "defensive"],
    discretionary: ["consumer cyclical", "consumer discretionary", "cyclical"],
    materials: ["basic materials", "materials"],
  };

  for (const [key, synonyms] of Object.entries(wordSynonyms)) {
    const aMatches = synonyms.some(syn => a.includes(syn)) || a.includes(key);
    const bMatches = synonyms.some(syn => b.includes(syn)) || b.includes(key);
    if (aMatches && bMatches) return true;
  }

  const genericWords = ["services", "industries", "industry", "sector", "group", "other", "company", "companies", "corporation", "corp", "incorporated"];
  const wordsA = a.split(/\s+/).filter(w => w.length >= 4 && !genericWords.includes(w));
  const wordsB = b.split(/\s+/).filter(w => w.length >= 4 && !genericWords.includes(w));

  for (const wA of wordsA) {
    for (const wB of wordsB) {
      if (wA === wB) return true;
      if (wA.startsWith(wB) || wB.startsWith(wA)) {
        const commonLen = Math.min(wA.length, wB.length);
        if (commonLen >= 5) return true;
      }
    }
  }

  return false;
}

export function getSimilarTheme(themeName: string, existingThemes: string[]): string | null {
  if (!themeName) return null;
  const match = existingThemes.find(t => areThemesSimilar(t, themeName));
  return match || null;
}

export function resolveThemeForItem(
  item: { theme?: string; analysis?: { sector?: string } },
  customThemes: string[],
  existingThemes: string[] = [],
  fallbackTheme: string = "Other"
): string {
  if (item.theme) return item.theme;
  const sector = item.analysis?.sector;
  if (!sector) return fallbackTheme;

  let similar = getSimilarTheme(sector, customThemes);
  if (similar) return similar;

  if (existingThemes.length > 0) {
    similar = getSimilarTheme(sector, existingThemes);
    if (similar) return similar;
  }

  return sector;
}

export function normalizeTicker(ticker: string | null | undefined): string {
  if (!ticker) return "";
  // v16.27: delegates to the shared pair validator. The previous rule appended "=X" to ANY
  // dotless six-character symbol, so a six-letter equity or ADR ticker was rewritten into a
  // currency pair that resolves to nothing.
  return normalizeForexTicker(ticker);
}

/**
 * Maps a recommendation or technical signal string to a numerical score for comparisons:
 * STRONG BUY: 2
 * BUY: 1
 * NEUTRAL / HOLD / DATA_UNAVAILABLE: 0
 * SELL: -1
 * STRONG SELL: -2
 */
export function getRatingScore(rec: string | null | undefined): number {
  if (!rec) return 0;
  const upper = rec.trim().toUpperCase();
  if (upper === "STRONG BUY") return 2;
  if (upper === "BUY") return 1;
  if (upper === "SELL") return -1;
  if (upper === "STRONG SELL") return -2;
  return 0;
}

/**
 * Determines whether a rating change from prevRec to currentRec is an upgrade or a downgrade.
 * - "STRONG BUY" -> "BUY": score goes 2 -> 1, so "downgraded"
 * - "BUY" -> "STRONG BUY": score goes 1 -> 2, so "upgraded"
 * - "STRONG SELL" -> "SELL": score goes -2 -> -1, so "upgraded"
 * - "SELL" -> "STRONG SELL": score goes -1 -> -2, so "downgraded"
 */
export function getRatingActionWord(prevRec: string | null | undefined, currentRec: string): 'upgraded' | 'downgraded' {
  const prevScore = getRatingScore(prevRec);
  const currentScore = getRatingScore(currentRec);
  return currentScore > prevScore ? 'upgraded' : 'downgraded';
}

export interface ExtendedMarketData {
  type: 'PRE' | 'POST';
  price: number;
  changePercent: number | null;
  labelKey: 'analysis.pre_market' | 'analysis.after_hours';
  shortLabel: string;
}

export function getExtendedMarketData(
  liveQ?: {
    marketState?: string | null;
    preMarketPrice?: number | null;
    preMarketChangePercent?: number | null;
    postMarketPrice?: number | null;
    postMarketChangePercent?: number | null;
  } | null,
  analysis?: {
    marketState?: string | null;
    preMarketPrice?: number | null;
    preMarketChangePercent?: number | null;
    postMarketPrice?: number | null;
    postMarketChangePercent?: number | null;
  } | null
): ExtendedMarketData | null {
  const mState = (liveQ?.marketState || analysis?.marketState || '').toUpperCase();
  
  const prePrice = liveQ?.preMarketPrice ?? analysis?.preMarketPrice;
  const preChange = liveQ?.preMarketChangePercent ?? analysis?.preMarketChangePercent ?? null;
  
  const postPrice = liveQ?.postMarketPrice ?? analysis?.postMarketPrice;
  const postChange = liveQ?.postMarketChangePercent ?? analysis?.postMarketChangePercent ?? null;

  const preData: ExtendedMarketData | null = typeof prePrice === 'number' ? {
    type: 'PRE',
    price: prePrice,
    changePercent: preChange,
    labelKey: 'analysis.pre_market',
    shortLabel: '🌅 Pre:',
  } : null;

  const postData: ExtendedMarketData | null = typeof postPrice === 'number' ? {
    type: 'POST',
    price: postPrice,
    changePercent: postChange,
    labelKey: 'analysis.after_hours',
    shortLabel: '🌙 After:',
  } : null;

  // 1. Explicit POST session -> Prefer post-market
  if (mState === 'POST' || mState === 'POSTPOST') {
    return postData || preData;
  }

  // 2. Explicit PRE session -> Prefer pre-market
  if (mState === 'PRE' || mState === 'PREPRE') {
    return preData || postData;
  }

  // 3. CLOSED state -> After 8 PM ET / overnight / weekends, the last extended session was post-market
  if (mState === 'CLOSED') {
    return postData || preData;
  }

  // 4. REGULAR hours state -> Pre-market occurred earlier today before open
  if (mState === 'REGULAR') {
    return preData || postData;
  }

  // 5. Fallback if marketState is unknown: prefer post-market if available (as post-market is later in the trading day)
  return postData || preData;
}

/**
 * Decimal places for rendering a price-like indicator level (SMA, VWMA, pivot).
 *
 * v16.27: these were hard-coded to `toFixed(0)` / `toFixed(2)`, calibrated to equity price
 * magnitudes. At FX magnitudes the rendered value collapsed to a constant -- EURUSD moving
 * averages displayed as `1 vs 1` on every bar while the underlying score crossed zero and
 * flipped the badge between BULLISH and BEARISH. Low-priced equities hit the same wall.
 * Precision is derived from the instrument's own magnitude, so no asset-class branch is
 * needed and penny stocks are fixed by the same change.
 */
export function indicatorDecimals(reference: number | null | undefined): number {
  const magnitude = Math.abs(Number(reference));
  if (!Number.isFinite(magnitude) || magnitude === 0) return 2;
  if (magnitude >= 100) return 2;
  if (magnitude >= 1) return 4;
  return 6;
}

/** Renders an indicator level at a precision appropriate to `reference` (usually the price). */
export function formatIndicatorLevel(
  value: number | null | undefined,
  reference: number | null | undefined
): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "N/A";
  return Number(value).toFixed(indicatorDecimals(reference));
}

/**
 * Renders a differential indicator (MACD, histogram) -- a difference of two moving averages,
 * so an order of magnitude smaller than price. Carries two extra decimals beyond the level
 * precision, which keeps equity MACD readable while making FX MACD non-degenerate.
 */
export function formatIndicatorDelta(
  value: number | null | undefined,
  reference: number | null | undefined
): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "N/A";
  return Number(value).toFixed(Math.min(indicatorDecimals(reference) + 2, 8));
}

/**
 * Abbreviates a share or contract volume for a compact readout.
 *
 * The chart legend previously abbreviated only at the million mark, so a mega-cap printed
 * `1250.00M` and a thinly traded name printed a seven-digit raw count next to four price fields.
 * Thousands and billions get their own suffix here; anything under a thousand is left as a
 * grouped integer, which is what a low-volume bar actually wants to show.
 */
const VOLUME_UNITS: ReadonlyArray<readonly [number, string]> = [
  [1e12, "T"],
  [1e9, "B"],
  [1e6, "M"],
  [1e3, "K"],
];

/**
 * Promotes a count to the next unit slightly before its exact threshold.
 *
 * At two decimals a value of 999,999 scales to 999.999K and prints as `1000.00K` -- a suffix that
 * has overflowed its own unit. Comparing against the point where the smaller unit would round to
 * 1000 promotes those to `1.00M` without costing precision anywhere else: 996,000 still prints as
 * `996.00K`, because it does not round up.
 */
const VOLUME_ROUNDING_HEADROOM = 0.999995;

export function formatCompactVolume(value: number | null | undefined): string {
  const volume = Number(value);
  if (value === null || value === undefined || !Number.isFinite(volume)) return "N/A";

  const magnitude = Math.abs(volume);
  for (const [scale, suffix] of VOLUME_UNITS) {
    if (magnitude >= scale * VOLUME_ROUNDING_HEADROOM) {
      return `${(volume / scale).toFixed(2)}${suffix}`;
    }
  }
  return volume.toLocaleString();
}
